import { z } from "zod";
import { getSupabaseClient } from "@/lib/supabase/client";

export const joinResultSchema = z.object({
  sessionId: z.string().uuid(),
  sessionGroupId: z.string().uuid(),
  participantId: z.string().uuid(),
  participantCode: z.string(),
  pseudonym: z.string(),
  classStudentId: z.string().optional(),
  status: z.enum(["joined", "active"]),
  consentStatus: z.enum(["not_required", "pending", "granted", "declined", "withdrawn"]),
  resumePath: z.string().startsWith("/play/"),
});
export type JoinResult = z.infer<typeof joinResultSchema>;

const prepareJoinResultSchema = z.discriminatedUnion("state", [
  joinResultSchema.extend({ state: z.literal("joined") }),
  z.object({
    state: z.literal("registration_required"),
    registrationOpen: z.boolean(),
    classPrefix: z.string(),
  }),
  z.object({ state: z.enum(["invalid_room", "closed", "different_class"]) }),
]);
export type PrepareJoinResult = z.infer<typeof prepareJoinResultSchema>;

const selfRegistrationResultSchema = joinResultSchema.extend({
  recoveryCode: z.string().optional(),
  isNewRegistration: z.boolean().optional(),
});

export const participantContextSchema = z.object({
  sessionId: z.string().uuid(),
  sessionGroupId: z.string().uuid(),
  participantId: z.string().uuid(),
  pseudonym: z.string(),
  status: z.enum(["joined", "active", "completed", "withdrawn"]),
  sessionStatus: z.enum(["draft", "scheduled", "open", "closed", "archived"]),
  consentStatus: z.enum(["not_required", "pending", "granted", "declined", "withdrawn"]),
  postTestEnabled: z.boolean(),
  preTestStatus: z.enum(["not_started", "in_progress", "completed", "abandoned"]),
  preTestScore: z.coerce.number().nullable(),
  postTestStatus: z.enum(["not_started", "in_progress", "completed", "abandoned"]),
  postTestScore: z.coerce.number().nullable(),
  activities: z.array(
    z.object({
      contentVersionId: z.string().uuid(),
      activityType: z.enum([
        "lesson",
        "flashcards",
        "scenario",
        "mapping",
        "reflection",
        "simulation",
      ]),
      title: z.string(),
      status: z.enum(["not_started", "in_progress", "completed", "abandoned"]),
      interactionMode: z.enum(["branching", "open_response"]).nullable(),
    }),
  ),
});
export type ParticipantContext = z.infer<typeof participantContextSchema>;

function client() {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

async function anonymousClient(captchaToken?: string) {
  const supabase = client();
  let { data: userData } = await supabase.auth.getUser();
  if (!userData.user) {
    if (!captchaToken) throw new Error("CAPTCHA_REQUIRED");
    const { data, error } = await supabase.auth.signInAnonymously({
      options: { captchaToken },
    });
    if (error) throw error;
    userData = { user: data.user };
  }
  if (!userData.user?.is_anonymous) {
    throw new Error("Sign out of the staff account before joining as a student.");
  }
  return supabase;
}

export async function prepareClassroomJoin(
  sessionCode: string,
  captchaToken?: string,
): Promise<PrepareJoinResult> {
  const supabase = await anonymousClient(captchaToken);
  const { data, error } = await supabase.rpc("prepare_learning_session_join", {
    requested_session_code: sessionCode,
  });
  if (error) throw error;
  return prepareJoinResultSchema.parse(data);
}

export async function selfRegisterClassroom(
  sessionCode: string,
  pseudonym: string,
): Promise<z.infer<typeof selfRegistrationResultSchema>> {
  const supabase = await anonymousClient();
  const { data, error } = await supabase.rpc("self_register_learning_session", {
    requested_session_code: sessionCode,
    requested_pseudonym: pseudonym,
  });
  if (error) throw error;
  return selfRegistrationResultSchema.parse(data);
}

export async function joinClassroom(sessionCode: string, rosterCode: string): Promise<JoinResult> {
  const supabase = await anonymousClient();
  const { data, error } = await supabase.rpc("join_learning_session", {
    requested_session_code: sessionCode,
    requested_roster_code: rosterCode,
  });
  if (error) throw error;
  const joinError = z.object({ errorCode: z.string(), errorMessage: z.string() }).safeParse(data);
  if (joinError.success) throw new Error(joinError.data.errorMessage);
  return joinResultSchema.parse(data);
}

export async function getParticipantContext(sessionId: string): Promise<ParticipantContext> {
  const { data, error } = await client().rpc("get_participant_context", {
    requested_session_id: sessionId,
  });
  if (error) throw error;
  return participantContextSchema.parse(data);
}

export async function decideConsent(
  participantId: string,
  status: "granted" | "declined",
): Promise<string> {
  const { data, error } = await client().rpc("record_roster_consent", {
    requested_participant_id: participantId,
    requested_status: status,
  });
  if (error) throw error;
  return z.object({ resumePath: z.string() }).parse(data).resumePath;
}
